import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Eye, FilePenLine, FilePlus2, Link2, Loader2, MessageCircle, Plus, Save, Send, ShoppingCart, Snowflake, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { SendPdfModal } from '../../../components/SendPdfModal';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { useConfirm } from '../../../hooks/use-confirm';
import { API_BASE_URL } from '../../../config/api';
import { legacyApi, type CalculateRequest, type Customer, type EstimateDetail, type InquiryDetail } from '../../../services/legacyApi';
import { getEstimateRef } from '../../../utils/estimateRefs';
import { openWhatsAppMessage } from '../utils/whatsapp';

type QuickQuoteMode = 'lump_sum' | 'itemized';

type QuickQuoteLine = {
    key: string;
    estimate_part_id?: number;
    part_ref_code?: string;
    part_name: string;
    quotation_description: string;
    quantity: number;
    unit_label: string;
    manual_unit_price: number;
    design_premium_perc: number;
};

type QuickQuotationEditorProps = {
    estimateId?: number;
    initialEstimate?: EstimateDetail | null;
};

const makeLine = (seed?: Partial<QuickQuoteLine>): QuickQuoteLine => ({
    key: `${Date.now()}-${Math.random()}`,
    part_name: '',
    quotation_description: '',
    quantity: 1,
    unit_label: 'Nos.',
    manual_unit_price: 0,
    design_premium_perc: 0,
    ...seed,
});

const toNumber = (value: string | number | null | undefined, fallback = 0) => {
    const numeric = typeof value === 'number' ? value : parseFloat(String(value ?? '').trim());
    return Number.isFinite(numeric) ? numeric : fallback;
};

const formatMoney = (value: number) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

const normalizeWhitespace = (value: string | null | undefined) =>
    String(value || '').replace(/\s+/g, ' ').trim();

const looksLongCommercialDescription = (value: string | null | undefined) => {
    const normalized = normalizeWhitespace(value);
    return normalized.length > 90 || String(value || '').includes('\n');
};

const deriveShortLineTitle = (projectDescription: string | null | undefined, description: string | null | undefined) => {
    const subject = normalizeWhitespace(projectDescription);
    if (subject) return subject.slice(0, 120);
    const normalizedDescription = normalizeWhitespace(description);
    if (!normalizedDescription) return 'Lump Sum Quote';
    const firstSentence = normalizedDescription.split(/[.!?]/)[0]?.trim() || normalizedDescription;
    return firstSentence.slice(0, 120);
};

const getStatusMeta = (approvalStatusId?: number) => {
    switch (approvalStatusId) {
        case 2:
            return { label: 'PENDING', className: 'bg-slate-100 text-slate-800' };
        case 3:
            return { label: 'SENT', className: 'bg-blue-100 text-blue-800' };
        case 4:
            return { label: 'APPROVED', className: 'bg-emerald-100 text-emerald-800' };
        case 5:
            return { label: 'ORDERED', className: 'bg-green-600 text-white' };
        case 6:
            return { label: 'REJECTED', className: 'bg-rose-100 text-rose-800' };
        case 7:
            return { label: 'SUPERSEDED', className: 'bg-amber-100 text-amber-800' };
        case 1:
        default:
            return { label: 'DRAFT', className: 'bg-slate-100 text-slate-800' };
    }
};

export const QuickQuotationEditor: React.FC<QuickQuotationEditorProps> = ({ estimateId, initialEstimate }) => {
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const [searchParams] = useSearchParams();
    const inquiryId = Number(searchParams.get('inquiryId') || 0) || undefined;

    const [loading, setLoading] = useState(!initialEstimate && (!!estimateId || !!inquiryId));
    const [saving, setSaving] = useState(false);
    const [busyAction, setBusyAction] = useState<string | null>(null);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [savedEstimateId, setSavedEstimateId] = useState<number | undefined>(estimateId);
    const [savedEstimateRef, setSavedEstimateRef] = useState<string | undefined>(initialEstimate?.estimate_ref);
    const [savedEstimateNo, setSavedEstimateNo] = useState<string | undefined>(initialEstimate?.estimate_no);
    const [savedRevisionNo, setSavedRevisionNo] = useState<number>(initialEstimate?.revision_no || 0);
    const [approvalStatusId, setApprovalStatusId] = useState<number>(initialEstimate?.approval_status_id || 1);
    const [isFreezed, setIsFreezed] = useState<boolean>(Boolean(initialEstimate?.is_freezed));
    const [primaryOrderId, setPrimaryOrderId] = useState<number | undefined>(initialEstimate?.related_orders?.[0]?.so_id);
    const [linkedCustomerId, setLinkedCustomerId] = useState<number | ''>('');
    const [customerName, setCustomerName] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [attention, setAttention] = useState('');
    const [enquiryRef, setEnquiryRef] = useState('');
    const [projectDescription, setProjectDescription] = useState('');
    const [validity, setValidity] = useState('15 Days from the date of Quotation');
    const [discountPercent, setDiscountPercent] = useState(0);
    const [vatPercent, setVatPercent] = useState(5);
    const [annexureA, setAnnexureA] = useState('');
    const [annexureB, setAnnexureB] = useState('');
    const [quoteMode, setQuoteMode] = useState<QuickQuoteMode>('lump_sum');
    const [lines, setLines] = useState<QuickQuoteLine[]>([makeLine({ unit_label: 'LS', quantity: 1 })]);
    const [isCustomerDialogOpen, setIsCustomerDialogOpen] = useState(false);
    const [showSendPdfModal, setShowSendPdfModal] = useState(false);
    const [customerSaving, setCustomerSaving] = useState(false);
    const [customerForm, setCustomerForm] = useState<Partial<Customer>>({});

    useEffect(() => {
        legacyApi.customers.list().then(setCustomers).catch((error) => {
            console.error(error);
            toast.error('Failed to load customers');
        });
    }, []);

    useEffect(() => {
        if (initialEstimate) {
            hydrateFromEstimate(initialEstimate);
            setLoading(false);
            return;
        }
        let cancelled = false;
        const run = async () => {
            try {
                if (estimateId) {
                    const detail = await legacyApi.estimates.getDetail(estimateId);
                    if (!cancelled) hydrateFromEstimate(detail);
                } else if (inquiryId) {
                    const inquiry = await legacyApi.inquiries.getDetail(inquiryId);
                    if (!cancelled) hydrateFromInquiry(inquiry);
                }
            } catch (error) {
                console.error(error);
                if (!cancelled) toast.error('Failed to load quick quotation context');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [estimateId, inquiryId, initialEstimate]);

    const hydrateFromEstimate = (detail: EstimateDetail) => {
        setSavedEstimateId(Number(detail.estimate_id || 0) || undefined);
        setSavedEstimateRef(detail.estimate_ref);
        setSavedEstimateNo(detail.estimate_no);
        setSavedRevisionNo(Number(detail.revision_no || 0));
        setApprovalStatusId(Number(detail.approval_status_id || 1));
        setIsFreezed(Boolean(detail.is_freezed));
        setPrimaryOrderId(Number(detail.related_orders?.[0]?.so_id || 0) || undefined);
        setLinkedCustomerId(detail.customer_id ?? '');
        setCustomerName(detail.customer_name_override || detail.customer_name || '');
        setCustomerAddress(detail.customer_address_override || detail.customer_address || '');
        setAttention(detail.attention || '');
        setEnquiryRef(detail.enquiry_ref || '');
        setProjectDescription(detail.project_description || '');
        setValidity(detail.validity || '15 Days from the date of Quotation');
        setDiscountPercent(detail.discount_percent || 0);
        setVatPercent(detail.vat_percent || 5);
        setAnnexureA(detail.annexure_a || '');
        setAnnexureB(detail.annexure_b || '');
        const savedParts = detail.parts || [];
        const nextMode = ((detail.quote_mode === 'lump_sum' && savedParts.length <= 1) ? 'lump_sum' : 'itemized') as QuickQuoteMode;
        setQuoteMode(nextMode);
        const quoteLines = savedParts.map((part, index) =>
            {
                const rawPartName = part.part_name || '';
                const rawQuotationDescription = part.quotation_description || '';
                const isLumpSumLine = nextMode === 'lump_sum';
                const quotationDescription = isLumpSumLine
                    ? (rawQuotationDescription || rawPartName)
                    : rawQuotationDescription;
                const partName = isLumpSumLine
                    ? (rawQuotationDescription ? rawPartName : deriveShortLineTitle(detail.project_description, rawPartName))
                    : rawPartName;
                return makeLine({
                    key: `${detail.estimate_id}-${part.estimate_part_id || index}`,
                    estimate_part_id: part.estimate_part_id,
                    part_ref_code: part.part_ref_code,
                    part_name: partName,
                    quotation_description: quotationDescription,
                    quantity: part.quantity || 1,
                    unit_label: part.unit_label || 'Nos.',
                    manual_unit_price: part.manual_unit_price || part.selling_price_per_unit || 0,
                    design_premium_perc: part.design_premium_perc || 0,
                });
            },
        );
        setLines(quoteLines.length > 0 ? quoteLines : [makeLine({ unit_label: nextMode === 'lump_sum' ? 'LS' : 'Nos.', quantity: 1 })]);
    };

    const hydrateFromInquiry = (inquiry: InquiryDetail) => {
        setLinkedCustomerId(inquiry.customer_id || '');
        setCustomerName(inquiry.customer_name || '');
        setCustomerAddress(inquiry.customer_address || '');
        setAttention(inquiry.attention || '');
        setEnquiryRef(inquiry.enquiry_ref || '');
        setProjectDescription(inquiry.project_description || inquiry.project_scope || '');
    };

    const totals = useMemo(() => {
        const activeLines = quoteMode === 'lump_sum' ? lines.slice(0, 1) : lines;
        const subtotal = activeLines.reduce((sum, line) => sum + (toNumber(line.quantity, 0) * toNumber(line.manual_unit_price, 0)), 0);
        const discountAmount = subtotal * (toNumber(discountPercent, 0) / 100);
        const afterDiscount = subtotal - discountAmount;
        const vatAmount = afterDiscount * (toNumber(vatPercent, 0) / 100);
        return {
            subtotal,
            discountAmount,
            afterDiscount,
            vatAmount,
            grandTotal: afterDiscount + vatAmount,
        };
    }, [discountPercent, lines, quoteMode, vatPercent]);

    const selectedCustomer = useMemo(
        () => customers.find((customer) => customer.customer_id === linkedCustomerId),
        [customers, linkedCustomerId],
    );

    useEffect(() => {
        if (quoteMode !== 'lump_sum') return;
        const defaultTitle = normalizeWhitespace(projectDescription);
        if (!defaultTitle) return;
        setLines((current) => {
            if (!current.length) return current;
            const firstLine = current[0];
            if (normalizeWhitespace(firstLine.part_name)) return current;
            return [{ ...firstLine, part_name: defaultTitle.slice(0, 120) }, ...current.slice(1)];
        });
    }, [projectDescription, quoteMode]);

    const validateQuickQuote = () => {
        if (quoteMode === 'lump_sum') {
            const line = lines[0];
            if (!normalizeWhitespace(line?.part_name)) {
                toast.error('Line Title is required for lump sum quotation.');
                return false;
            }
        }
        return true;
    };

    const buildPayload = (entryModeOverride?: 'standard' | 'quick_quote'): CalculateRequest => {
        const normalizedLines = (quoteMode === 'lump_sum' ? lines.slice(0, 1) : lines).map((line, index) => {
            const resolvedPartName = quoteMode === 'lump_sum'
                ? (normalizeWhitespace(line.part_name) || deriveShortLineTitle(projectDescription, line.quotation_description))
                : (line.part_name || `Line ${index + 1}`);
            const resolvedQuotationDescription = quoteMode === 'lump_sum'
                ? (normalizeWhitespace(line.quotation_description) || normalizeWhitespace(line.part_name) || normalizeWhitespace(projectDescription) || 'Lump Sum Quote')
                : normalizeWhitespace(line.quotation_description) || undefined;
            return {
                estimate_part_id: line.estimate_part_id,
                part_name: resolvedPartName,
                quotation_description: resolvedQuotationDescription,
                dimension: '',
                quantity: quoteMode === 'lump_sum' ? 1 : Math.max(toNumber(line.quantity, 1), 0),
                area_m2: 0,
                thickness: 0,
                weight_kgs: 0,
                mould_area: 0,
                mould_cost_m2: 0,
                m2_per_hour_production: 0,
                man_hours_required: 0,
                bom_cost: 0,
                material_type_id: 0,
                pricing_mode: 'manual_quote',
                manual_unit_price: Math.max(toNumber(line.manual_unit_price, 0), 0),
                unit_label: line.unit_label || (quoteMode === 'lump_sum' ? 'LS' : 'Nos.'),
                design_premium_perc: toNumber(line.design_premium_perc, 0),
                part_ref_code: line.part_ref_code,
                cost_entry_mode: 'detailed',
                simple_raw_material_cost: 0,
                simple_accessories_cost: 0,
                simple_labour_cost: 0,
                simple_overhead_cost: 0,
                installation_cost: 0,
                packing_cost: 0,
                freight_cost: 0,
                project_fee: 0,
                admin_cost: 0,
                bom_items: [],
            };
        });

        return {
            header: {
                customer_id: linkedCustomerId === '' ? undefined : Number(linkedCustomerId),
                customer_name: selectedCustomer?.customer_name,
                customer_name_override: customerName.trim() || undefined,
                customer_address: customerAddress.trim() || undefined,
                customer_address_override: customerAddress.trim() || undefined,
                project_description: projectDescription.trim() || undefined,
                attention: attention.trim() || undefined,
                enquiry_ref: enquiryRef.trim() || undefined,
                validity: validity.trim(),
                annexure_a: annexureA.trim(),
                annexure_b: annexureB.trim(),
                entry_mode: entryModeOverride || 'quick_quote',
                quote_mode: quoteMode,
                labour_rate_per_hour: 0,
                overhead_per_sqm: 0,
                oh_per_sqm: 0,
                profit_margin: 0,
                discount_percent: toNumber(discountPercent, 0),
                vat_percent: toNumber(vatPercent, 0),
                installation_in_scope: false,
            },
            parts: normalizedLines,
            installation_items: [],
        };
    };

    const saveDraft = async (entryModeOverride?: 'standard' | 'quick_quote') => {
        if (!validateQuickQuote()) return undefined;
        setSaving(true);
        try {
            const payload = buildPayload(entryModeOverride);
            if (savedEstimateId) {
                await legacyApi.estimates.update(savedEstimateId, payload);
                const refreshed = await legacyApi.estimates.getDetail(savedEstimateId);
                hydrateFromEstimate(refreshed);
                toast.success(entryModeOverride === 'standard' ? 'Converted to full estimate' : 'Quick quotation updated');
                return savedEstimateId;
            }
            const created = await legacyApi.estimates.create(payload);
            const newId = created.estimate_id;
            setSavedEstimateId(newId);
            setSavedEstimateRef(created.estimate_ref);
            setSavedEstimateNo(created.estimate_no);
            setSavedRevisionNo(created.revision_no || 0);
            toast.success('Quick quotation created');
            navigate(`/legacy/estimates/${newId}`, { replace: true });
            return newId;
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to save quick quotation');
            return undefined;
        } finally {
            setSaving(false);
        }
    };

    const handlePreview = async () => {
        const id = savedEstimateId || await saveDraft();
        if (!id) return;
        window.open(`${API_BASE_URL}/estimates/${id}/print`, '_blank');
    };

    const handlePdf = async () => {
        const id = savedEstimateId || await saveDraft();
        if (!id) return;
        await legacyApi.estimates.downloadPDF(id);
    };

    const handleOpenSendPdfModal = async () => {
        const id = savedEstimateId || await saveDraft();
        if (!id) return;
        setShowSendPdfModal(true);
    };

    const handleSendPdf = async (payload: { to: string; cc?: string; subject?: string; body?: string }) => {
        if (!savedEstimateId) {
            throw new Error('Save the quotation before sending PDF');
        }
        const result = await legacyApi.estimates.emailPdf(savedEstimateId, payload);
        toast.success(result.message || 'Quotation PDF emailed successfully');
    };

    const handleShare = async () => {
        const id = savedEstimateId || await saveDraft();
        if (!id) return;
        setBusyAction('share');
        try {
            const result = await legacyApi.estimates.createShareLink(id, {});
            try {
                await navigator.clipboard.writeText(result.share_url);
                toast.success(result.is_new ? 'Quotation share link created and copied.' : 'Quotation share link copied.');
            } catch {
                window.prompt('Copy quotation share link', result.share_url);
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to create share link');
        } finally {
            setBusyAction(null);
        }
    };

    const handleShareWhatsApp = async () => {
        const id = savedEstimateId || await saveDraft();
        if (!id) return;
        setBusyAction('whatsapp');
        try {
            const result = await legacyApi.estimates.createShareLink(id, { channel: 'WHATSAPP' });
            const message = `Please find quotation ${displayEstimateRef}: ${result.share_url}`;
            const resolved = openWhatsAppMessage(message, selectedCustomer);
            if (resolved.normalizedNumber) {
                toast.success(result.is_new ? 'Quotation share link created for WhatsApp.' : 'Quotation share link opened in WhatsApp.');
            } else {
                toast.warning('No valid customer WhatsApp number found. Opened WhatsApp without a selected recipient.');
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to open WhatsApp share');
        } finally {
            setBusyAction(null);
        }
    };

    const handleRevise = async () => {
        if (!savedEstimateId) {
            toast.error('Save the quotation before creating a revision');
            return;
        }
        setBusyAction('revise');
        try {
            const revised = await legacyApi.estimates.revise(savedEstimateId);
            navigate(`/legacy/estimates/${revised.estimate_id}`);
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to create revision');
        } finally {
            setBusyAction(null);
        }
    };

    const handleConvertToFullEstimate = async () => {
        const id = await saveDraft('standard');
        if (!id) return;
        navigate(`/legacy/estimates/${id}?forceClassic=1&tab=inputs`);
    };

    const handleSubmitQuote = async () => {
        if (!canSendQuote) {
            toast.error('Link a saved customer and freeze the quotation before marking it as sent.');
            return;
        }
        const id = savedEstimateId;
        if (!id) return;
        setBusyAction('submit');
        try {
            await legacyApi.estimates.markSent(id);
            const refreshed = await legacyApi.estimates.getDetail(id);
            hydrateFromEstimate(refreshed);
            toast.success('Quotation submitted and ready for sale order conversion');
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to submit quotation');
        } finally {
            setBusyAction(null);
        }
    };

    const handleDelete = async () => {
        if (!savedEstimateId) return;
        if (!(await confirm(
            'Are you sure you want to delete this quick quotation? This action cannot be undone.',
            {
                title: 'Delete Quotation',
                confirmText: 'Delete',
                variant: 'destructive',
            },
        ))) return;
        setBusyAction('delete');
        try {
            await legacyApi.estimates.delete(savedEstimateId);
            toast.success('Quick quotation deleted successfully');
            navigate('/legacy/estimates');
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to delete quick quotation');
        } finally {
            setBusyAction(null);
        }
    };

    const handleFreeze = async () => {
        if (!isFreezed && !canConvertToOrder) {
            toast.error('Create and link a saved customer before freezing this quotation.');
            return;
        }
        const id = isFreezed ? savedEstimateId : await saveDraft();
        if (!id) return;
        setBusyAction('freeze');
        try {
            const updated = await legacyApi.estimates.freeze(id, !isFreezed);
            const refreshed = await legacyApi.estimates.getDetail(id);
            hydrateFromEstimate(refreshed);
            setIsFreezed(Boolean(updated.is_freezed));
            toast.success(updated.is_freezed ? 'Quotation frozen' : 'Quotation unfrozen');
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to update freeze state');
        } finally {
            setBusyAction(null);
        }
    };

    const handleContinueToSaleOrder = async () => {
        if (!canConvertToOrder) {
            toast.error(
                normalizedStatusId === 3
                    ? 'This sent quotation has no linked customer. Create a revision, link the customer, then continue to Sale Order.'
                    : 'Link a saved customer before continuing to Sale Order.',
            );
            return;
        }
        if (primaryOrderId) {
            navigate(`/legacy/orders/${primaryOrderId}`);
            return;
        }
        if (!canContinueToSaleOrder) {
            toast.error('Freeze and send the quotation before continuing to Sale Order.');
            return;
        }
        const id = savedEstimateId;
        if (!id) return;
        setBusyAction('convert-order');
        try {
            const order = await legacyApi.sales.convertEstimate({ estimate_id: id });
            toast.success('Sale order created');
            navigate(`/legacy/orders/${order.so_id}`);
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to continue to sale order');
        } finally {
            setBusyAction(null);
        }
    };

    const updateLine = (key: string, patch: Partial<QuickQuoteLine>) => {
        setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
    };

    const removeLine = (key: string) => {
        setLines((current) => {
            if (current.length <= 1) return current;
            return current.filter((line) => line.key !== key);
        });
    };

    const addLine = () => {
        setLines((current) => [...current, makeLine()]);
    };

    const openCreateCustomerDialog = () => {
        setCustomerForm({
            customer_name: customerName.trim() || '',
            poc_name: attention.trim() || '',
            address: customerAddress.trim() || '',
        });
        setIsCustomerDialogOpen(true);
    };

    const handleCreateCustomer = async () => {
        const name = customerForm.customer_name?.trim();
        if (!name) {
            toast.error('Customer name is required');
            return;
        }
        setCustomerSaving(true);
        try {
            const created = await legacyApi.customers.create({
                customer_name: name,
                poc_name: customerForm.poc_name?.trim() || undefined,
                poc_contact: customerForm.poc_contact?.trim() || undefined,
                email_address: customerForm.email_address?.trim() || undefined,
                emirate: customerForm.emirate?.trim() || undefined,
                address: customerForm.address?.trim() || undefined,
                trn: customerForm.trn?.trim() || undefined,
            });
            setCustomers((current) => [...current, created].sort((a, b) => a.customer_name.localeCompare(b.customer_name)));
            setLinkedCustomerId(created.customer_id);
            setCustomerName(created.customer_name || customerName);
            setCustomerAddress(created.address || customerAddress);
            if (!attention.trim() && created.poc_name) setAttention(created.poc_name);
            setIsCustomerDialogOpen(false);
            toast.success('Customer created and linked to this quotation');
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to create customer');
        } finally {
            setCustomerSaving(false);
        }
    };

    const normalizedStatusId = Number(approvalStatusId || 0);
    const canConvertToOrder = linkedCustomerId !== '' && Number(linkedCustomerId) > 0;
    const isSubmitted = normalizedStatusId >= 3;
    const canEditQuote = (!savedEstimateId || (!isFreezed && normalizedStatusId !== 4 && normalizedStatusId !== 5));
    const canFreezeQuote = Boolean(savedEstimateId) && (normalizedStatusId === 1 || normalizedStatusId === 2) && (isFreezed || canConvertToOrder);
    const canSendQuote = Boolean(savedEstimateId) && isFreezed && canConvertToOrder && (normalizedStatusId === 1 || normalizedStatusId === 2);
    const canCreateRevision = Boolean(savedEstimateId) && isFreezed && normalizedStatusId === 3;
    const canContinueToSaleOrder = Boolean(savedEstimateId) && isFreezed && normalizedStatusId === 3 && canConvertToOrder && !primaryOrderId;
    const sentWithoutCustomerLink = Boolean(savedEstimateId) && normalizedStatusId === 3 && !primaryOrderId && !canConvertToOrder;
    const freezeBlockedByMissingCustomer = Boolean(savedEstimateId) && !canConvertToOrder && (normalizedStatusId === 1 || normalizedStatusId === 2);
    const shouldWarnBeforeSendForCustomerLink = !canConvertToOrder && (normalizedStatusId === 1 || normalizedStatusId === 2 || !savedEstimateId);
    const statusMeta = getStatusMeta(approvalStatusId);
    const displayEstimateRef = savedEstimateId
        ? (getEstimateRef({
            estimate_id: savedEstimateId,
            estimate_ref: savedEstimateRef,
            estimate_no: savedEstimateNo,
            revision_no: savedRevisionNo,
        }) || `Estimate ${savedEstimateId}`)
        : 'Quick Quotation';
    const quoteAttachmentName = `${displayEstimateRef.replace(/[\\/:*?"<>|]+/g, '_')}.pdf`;
    const quoteRecipientEmail = selectedCustomer?.email_address || '';

    if (loading) {
        return <div className="p-8 text-center text-muted-foreground">Loading quick quotation...</div>;
    }

    return (
        <div className="space-y-6 p-6">
            <SendPdfModal
                open={showSendPdfModal}
                onOpenChange={setShowSendPdfModal}
                documentLabel="Quotation"
                documentNumber={displayEstimateRef}
                attachmentName={quoteAttachmentName}
                defaultTo={quoteRecipientEmail}
                defaultSubject={`Quotation ${displayEstimateRef}`}
                defaultBody={`Dear Customer,\n\nPlease find attached quotation ${displayEstimateRef} for your reference.\n\nRegards,\nHADAF COMPOSITES`}
                onSend={handleSendPdf}
            />
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-2">
                    <Button variant="ghost" className="px-0 text-sm" onClick={() => navigate('/legacy/estimates')}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Estimates
                    </Button>
                    <div className="flex items-center gap-3">
                        <h1 className="text-3xl font-bold tracking-tight text-foreground">{displayEstimateRef}</h1>
                        <span className="text-sm text-muted-foreground">Rev {savedRevisionNo}</span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusMeta.className}`}>
                            {statusMeta.label}
                        </span>
                        <Badge variant="secondary">Quick Quote</Badge>
                        <Badge variant="outline">{quoteMode === 'lump_sum' ? 'Lump Sum' : 'Itemized'}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        Capture the commercial quotation first. For ad hoc customers, you can still preview, share, and save the quote, but a saved customer must be linked before it can be frozen and continue into Sale Order.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {canFreezeQuote ? (
                        <Button
                            variant="outline"
                            className={isFreezed ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900' : undefined}
                            onClick={handleFreeze}
                            disabled={busyAction === 'freeze'}
                        >
                            {busyAction === 'freeze' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Snowflake className="mr-2 h-4 w-4" />}
                            {isFreezed ? 'Unfreeze' : 'Freeze'}
                        </Button>
                    ) : null}
                    {canSendQuote ? (
                        <Button onClick={handleSubmitQuote} disabled={!canSendQuote || busyAction === 'submit'}>
                            {busyAction === 'submit' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                            Mark as Sent
                        </Button>
                    ) : null}
                    <Button variant="outline" onClick={handlePreview}>
                        <Eye className="mr-2 h-4 w-4" />
                        Preview
                    </Button>
                    <Button variant="outline" onClick={handlePdf}>
                        <Download className="mr-2 h-4 w-4" />
                        PDF
                    </Button>
                    <Button variant="outline" onClick={handleOpenSendPdfModal}>
                        <Send className="mr-2 h-4 w-4" />
                        Send
                    </Button>
                    <Button variant="outline" onClick={handleShare} disabled={busyAction === 'share'}>
                        {busyAction === 'share' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
                        Share Link
                    </Button>
                    <Button variant="outline" onClick={handleShareWhatsApp} disabled={busyAction === 'whatsapp'}>
                        {busyAction === 'whatsapp' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-2 h-4 w-4" />}
                        WhatsApp
                    </Button>
                    {!canConvertToOrder && canEditQuote ? (
                        <Button variant="outline" onClick={openCreateCustomerDialog}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Customer
                        </Button>
                    ) : null}
                    {canCreateRevision ? (
                        <Button
                            variant="outline"
                            className="border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800"
                            onClick={handleRevise}
                            disabled={busyAction === 'revise'}
                        >
                            {busyAction === 'revise' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FilePlus2 className="mr-2 h-4 w-4" />}
                            {sentWithoutCustomerLink ? 'Revise to Link Customer' : 'Revise'}
                        </Button>
                    ) : null}
                    <Button
                        variant={primaryOrderId ? 'outline' : 'default'}
                        onClick={handleContinueToSaleOrder}
                        disabled={(!primaryOrderId && !canContinueToSaleOrder) || busyAction === 'convert-order'}
                    >
                        {busyAction === 'convert-order' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShoppingCart className="mr-2 h-4 w-4" />}
                        {primaryOrderId ? 'Open Sale Order' : 'Continue to Sale Order'}
                    </Button>
                    <Button variant="outline" onClick={handleConvertToFullEstimate} disabled={!canEditQuote}>
                        <FilePenLine className="mr-2 h-4 w-4" />
                        Convert to Full Estimate
                    </Button>
                    <Button onClick={() => void saveDraft()} disabled={saving || !canEditQuote}>
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Save Draft
                    </Button>
                    {savedEstimateId ? (
                        <Button
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={handleDelete}
                            disabled={busyAction === 'delete'}
                        >
                            {busyAction === 'delete' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                            Delete
                        </Button>
                    ) : null}
                </div>
            </div>
            <div className="grid gap-6 xl:grid-cols-[1.7fr,0.9fr]">
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Quotation Header</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-2">
                            <label className="space-y-2 text-sm font-medium">
                                Linked Customer
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={linkedCustomerId}
                                    disabled={!canEditQuote}
                                    onChange={(event) => {
                                        const value = event.target.value ? Number(event.target.value) : '';
                                        setLinkedCustomerId(value);
                                        const customer = customers.find((row) => row.customer_id === value);
                                        if (customer) {
                                            if (!customerName.trim()) setCustomerName(customer.customer_name || '');
                                            if (!customerAddress.trim()) setCustomerAddress(customer.address || '');
                                        }
                                    }}
                                >
                                    <option value="">Ad hoc / manual customer</option>
                                    {customers.map((customer) => (
                                        <option key={customer.customer_id} value={customer.customer_id}>
                                            {customer.customer_name}
                                        </option>
                                    ))}
                                </select>
                                {sentWithoutCustomerLink ? (
                                    <p className="text-xs font-normal text-amber-700">
                                        This quotation was sent with ad hoc customer details only. Create a revision, link a saved customer here, then send again to continue to Sale Order.
                                    </p>
                                ) : freezeBlockedByMissingCustomer ? (
                                    <p className="text-xs font-normal text-amber-700">
                                        Ad hoc customer is fine for quotation and PDF, but this quote cannot be frozen until you create and link a saved customer.
                                    </p>
                                ) : shouldWarnBeforeSendForCustomerLink ? (
                                    <p className="text-xs font-normal text-muted-foreground">
                                        Ad hoc customer details are valid for quotation and PDF. Create a saved customer before Freeze so the quote can continue through Sale Order, delivery, and invoice.
                                    </p>
                                ) : null}
                                {!canConvertToOrder && canEditQuote ? (
                                    <div className="pt-1">
                                        <Button type="button" variant="outline" size="sm" onClick={openCreateCustomerDialog}>
                                            <Plus className="mr-2 h-4 w-4" />
                                            Create Customer From Quote
                                        </Button>
                                    </div>
                                ) : null}
                            </label>
                            <label className="space-y-2 text-sm font-medium">
                                Quote Mode
                                <div className="flex gap-2">
                                    <Button type="button" variant={quoteMode === 'lump_sum' ? 'default' : 'outline'} onClick={() => setQuoteMode('lump_sum')} disabled={!canEditQuote}>
                                        Lump Sum
                                    </Button>
                                    <Button type="button" variant={quoteMode === 'itemized' ? 'default' : 'outline'} onClick={() => setQuoteMode('itemized')} disabled={!canEditQuote}>
                                        Itemized
                                    </Button>
                                </div>
                            </label>
                            <label className="space-y-2 text-sm font-medium">
                                Customer Name
                                <Input value={customerName} onChange={(event) => setCustomerName(event.target.value)} placeholder="Customer display name" disabled={!canEditQuote} />
                            </label>
                            <label className="space-y-2 text-sm font-medium">
                                Attention
                                <Input value={attention} onChange={(event) => setAttention(event.target.value)} placeholder="Contact person" disabled={!canEditQuote} />
                            </label>
                            <label className="space-y-2 text-sm font-medium md:col-span-2">
                                Customer Address
                                <Textarea value={customerAddress} onChange={(event) => setCustomerAddress(event.target.value)} rows={3} placeholder="Address shown on the quotation" disabled={!canEditQuote} />
                            </label>
                            <label className="space-y-2 text-sm font-medium">
                                Enquiry Reference
                                <Input value={enquiryRef} onChange={(event) => setEnquiryRef(event.target.value)} placeholder="Mail ref / WhatsApp ref / PO reference" disabled={!canEditQuote} />
                            </label>
                            <label className="space-y-2 text-sm font-medium">
                                Validity
                                <Input value={validity} onChange={(event) => setValidity(event.target.value)} placeholder="15 Days from the date of Quotation" disabled={!canEditQuote} />
                            </label>
                            <label className="space-y-2 text-sm font-medium md:col-span-2">
                                Project Description / Subject
                                <Textarea value={projectDescription} onChange={(event) => setProjectDescription(event.target.value)} rows={3} placeholder="Short quotation subject or scope" disabled={!canEditQuote} />
                            </label>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Commercial Lines</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {lines.slice(0, quoteMode === 'lump_sum' ? 1 : lines.length).map((line, index) => {
                                const amount = toNumber(line.quantity, 0) * toNumber(line.manual_unit_price, 0);
                                return (
                                    <div key={line.key} className="rounded-lg border border-border p-4">
                                        <div className="mb-3 flex items-center justify-between">
                                            <div className="text-sm font-semibold text-foreground">
                                                {quoteMode === 'lump_sum' ? 'Lump Sum Line' : `Line ${index + 1}`}
                                            </div>
                                            {quoteMode === 'itemized' ? (
                                                <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(line.key)} disabled={!canEditQuote}>
                                                    <Trash2 className="h-4 w-4 text-red-600" />
                                                </Button>
                                            ) : null}
                                        </div>
                                        <div className="grid gap-4 md:grid-cols-12">
                                            <label className="space-y-2 text-sm font-medium md:col-span-5">
                                                {quoteMode === 'lump_sum' ? 'Line Title' : 'Description'}
                                                {quoteMode === 'lump_sum' ? (
                                                    <Input
                                                        value={line.part_name}
                                                        onChange={(event) => updateLine(line.key, { part_name: event.target.value })}
                                                        placeholder="Short internal item name"
                                                        disabled={!canEditQuote}
                                                    />
                                                ) : (
                                                    <Textarea
                                                        value={line.part_name}
                                                        onChange={(event) => updateLine(line.key, { part_name: event.target.value })}
                                                        rows={2}
                                                        placeholder="Line description"
                                                        disabled={!canEditQuote}
                                                    />
                                                )}
                                            </label>
                                            <label className="space-y-2 text-sm font-medium md:col-span-2">
                                                Qty
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    value={quoteMode === 'lump_sum' ? 1 : line.quantity}
                                                    onChange={(event) => updateLine(line.key, { quantity: toNumber(event.target.value, 1) })}
                                                    disabled={quoteMode === 'lump_sum' || !canEditQuote}
                                                />
                                            </label>
                                            <label className="space-y-2 text-sm font-medium md:col-span-2">
                                                Unit
                                                <Input value={line.unit_label} onChange={(event) => updateLine(line.key, { unit_label: event.target.value })} placeholder="Nos. / LS / Lot" disabled={!canEditQuote} />
                                            </label>
                                            <label className="space-y-2 text-sm font-medium md:col-span-3">
                                                Unit Price
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={line.manual_unit_price}
                                                    onChange={(event) => updateLine(line.key, { manual_unit_price: toNumber(event.target.value, 0) })}
                                                    disabled={!canEditQuote}
                                                />
                                            </label>
                                        </div>
                                        {quoteMode === 'lump_sum' ? (
                                            <label className="mt-4 block space-y-2 text-sm font-medium">
                                                Quotation Description
                                                <Textarea
                                                    value={line.quotation_description}
                                                    onChange={(event) => updateLine(line.key, { quotation_description: event.target.value })}
                                                    rows={6}
                                                    placeholder="Detailed customer-facing description shown on quotation and sale order print"
                                                    disabled={!canEditQuote}
                                                />
                                            </label>
                                        ) : null}
                                        <div className="mt-3 text-sm text-muted-foreground">
                                            Amount: <span className="font-semibold text-foreground">AED {formatMoney(amount)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                            {quoteMode === 'itemized' ? (
                                <div className="flex justify-end pt-2">
                                    <Button type="button" variant="outline" size="sm" onClick={addLine} disabled={!canEditQuote}>
                                        <Plus className="mr-2 h-4 w-4" />
                                        Add Line
                                    </Button>
                                </div>
                            ) : null}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Commercial Notes</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-2">
                            <label className="space-y-2 text-sm font-medium">
                                Annexure A
                                <Textarea value={annexureA} onChange={(event) => setAnnexureA(event.target.value)} rows={5} placeholder="Optional annexure or commercial notes" />
                            </label>
                            <label className="space-y-2 text-sm font-medium">
                                Terms &amp; Conditions
                                <Textarea value={annexureB} onChange={(event) => setAnnexureB(event.target.value)} rows={5} />
                            </label>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Commercial Totals</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm">
                            <div className="flex items-center justify-between">
                                <span>Subtotal</span>
                                <span className="font-semibold">AED {formatMoney(totals.subtotal)}</span>
                            </div>
                            <label className="space-y-2">
                                <span className="font-medium">Discount (%)</span>
                                <Input type="number" min="0" step="0.01" value={discountPercent} onChange={(event) => setDiscountPercent(toNumber(event.target.value, 0))} disabled={!canEditQuote} />
                            </label>
                            <div className="flex items-center justify-between">
                                <span>Discount Amount</span>
                                <span className="font-semibold text-red-600">AED {formatMoney(totals.discountAmount)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span>Amount After Discount</span>
                                <span className="font-semibold">AED {formatMoney(totals.afterDiscount)}</span>
                            </div>
                            <label className="space-y-2">
                                <span className="font-medium">VAT (%)</span>
                                <Input type="number" min="0" step="0.01" value={vatPercent} onChange={(event) => setVatPercent(toNumber(event.target.value, 0))} disabled={!canEditQuote} />
                            </label>
                            <div className="flex items-center justify-between">
                                <span>VAT Amount</span>
                                <span className="font-semibold">AED {formatMoney(totals.vatAmount)}</span>
                            </div>
                            <div className="rounded-lg bg-primary px-4 py-3 text-primary-foreground">
                                <div className="text-xs uppercase tracking-[0.25em] opacity-80">Grand Total</div>
                                <div className="mt-1 text-2xl font-bold">AED {formatMoney(totals.grandTotal)}</div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Workflow Note</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm text-muted-foreground">
                            <p>
                                Quick quotations can be saved, previewed, revised, shared, and converted later into a full estimate.
                            </p>
                            <p>
                                Sale Order conversion follows the same flow as the full estimate, but quick quotes now require a saved customer before Freeze so downstream delivery and invoice workflows stay aligned.
                            </p>
                            <p>
                                Follow the same workflow as a full estimate: save your changes, <span className="font-medium text-foreground">Freeze</span> the quotation, <span className="font-medium text-foreground">Send</span> it, then continue to <span className="font-medium text-foreground">Sale Order</span> or create a revision.
                            </p>
                            {!canEditQuote ? (
                                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                                    This quotation is locked by workflow. Create a revision to make changes.
                                </div>
                            ) : null}
                            {sentWithoutCustomerLink ? (
                                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                                    Sale Order is currently blocked because this sent quotation has no linked customer record. Next step: <span className="font-medium">Revise</span>, link the saved customer, <span className="font-medium">Freeze</span>, <span className="font-medium">Send</span>, then continue to <span className="font-medium">Sale Order</span>.
                                </div>
                            ) : freezeBlockedByMissingCustomer ? (
                                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                                    Freeze is intentionally blocked while this quote uses ad hoc customer details only. Use <span className="font-medium">Create Customer</span> first so the linked customer ID is ready for the full sales cycle.
                                </div>
                            ) : null}
                            <div className="rounded-md border border-dashed border-border px-3 py-2 text-foreground">
                                {primaryOrderId
                                    ? `Sale order already created: HC/SO/${String(primaryOrderId).padStart(4, '0')}`
                                    : canConvertToOrder
                                        ? 'Customer linked: this quotation can continue into the sales cycle.'
                                        : 'No linked customer yet: quotation is valid, but Sale Order conversion will stay blocked.'}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
            <Dialog open={isCustomerDialogOpen} onOpenChange={setIsCustomerDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Create Customer From Quote</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 md:grid-cols-2">
                        <label className="space-y-2 text-sm font-medium md:col-span-2">
                            Customer Name
                            <Input
                                value={customerForm.customer_name || ''}
                                onChange={(event) => setCustomerForm((current) => ({ ...current, customer_name: event.target.value }))}
                                placeholder="Customer name"
                            />
                        </label>
                        <label className="space-y-2 text-sm font-medium">
                            Contact Person
                            <Input
                                value={customerForm.poc_name || ''}
                                onChange={(event) => setCustomerForm((current) => ({ ...current, poc_name: event.target.value }))}
                                placeholder="Primary contact"
                            />
                        </label>
                        <label className="space-y-2 text-sm font-medium">
                            Contact Number
                            <Input
                                value={customerForm.poc_contact || ''}
                                onChange={(event) => setCustomerForm((current) => ({ ...current, poc_contact: event.target.value }))}
                                placeholder="Phone number"
                            />
                        </label>
                        <label className="space-y-2 text-sm font-medium">
                            Email
                            <Input
                                value={customerForm.email_address || ''}
                                onChange={(event) => setCustomerForm((current) => ({ ...current, email_address: event.target.value }))}
                                placeholder="Email address"
                            />
                        </label>
                        <label className="space-y-2 text-sm font-medium">
                            Emirate
                            <Input
                                value={customerForm.emirate || ''}
                                onChange={(event) => setCustomerForm((current) => ({ ...current, emirate: event.target.value }))}
                                placeholder="Emirate"
                            />
                        </label>
                        <label className="space-y-2 text-sm font-medium md:col-span-2">
                            Address
                            <Textarea
                                value={customerForm.address || ''}
                                onChange={(event) => setCustomerForm((current) => ({ ...current, address: event.target.value }))}
                                rows={4}
                                placeholder="Customer address"
                            />
                        </label>
                        <label className="space-y-2 text-sm font-medium">
                            TRN
                            <Input
                                value={customerForm.trn || ''}
                                onChange={(event) => setCustomerForm((current) => ({ ...current, trn: event.target.value }))}
                                placeholder="TRN"
                            />
                        </label>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setIsCustomerDialogOpen(false)} disabled={customerSaving}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={handleCreateCustomer} disabled={customerSaving}>
                            {customerSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                            Create and Link Customer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
